'use client'

import { ListHeader, Image } from '@macarongfactory/web-ui'
import Link from 'next/link'
import { useRef } from 'react'
import { Swiper, SwiperSlide } from 'swiper/react'

import { ROUTES } from '@/shared/lib/routes'
import { OptimizedImage } from '@/shared/ui'

import { type ICuratedItem, SWIPER_HORIZONTAL_PADDING_STYLE } from '../lib'
import useTrackOnVisible from '../model/useTrackOnVisible'

interface ICuratedSwiperSectionProps {
  title: string
  description: string
  items: ICuratedItem[]
  /** 카드 노출 시 호출 (1회) */
  onCardShow?: (item: ICuratedItem, index: number) => void
  /** 카드 클릭 시 호출 */
  onCardClick?: (item: ICuratedItem, index: number) => void
  /** 첫 스크롤 시 호출 (1회) */
  onFirstSwipe?: () => void
}

const CuratedSwiperSection = ({
  title,
  description,
  items,
  onCardShow,
  onCardClick,
  onFirstSwipe,
}: ICuratedSwiperSectionProps) => {
  const hasSwipedRef = useRef(false)

  const handleSlideChange = () => {
    if (hasSwipedRef.current || !onFirstSwipe) return
    hasSwipedRef.current = true
    onFirstSwipe()
  }

  return (
    <section className='pb-dimension-x10'>
      <ListHeader
        title={
          <ListHeader.Main.Title.Text
            text={title}
            typography='heading-3-bold'
          />
        }
        lower={
          <ListHeader.Main.Lower.Text
            text={description}
            typography='body-1-medium'
          />
        }
      />
      <Swiper
        slidesPerView='auto'
        spaceBetween={12}
        initialSlide={0}
        onSlideChange={handleSlideChange}
        style={SWIPER_HORIZONTAL_PADDING_STYLE}
      >
        {items.map((item, index) => (
          <SwiperSlide key={item.id} className='w-66! md:w-100!'>
            <CuratedCard
              item={item}
              index={index}
              onShow={onCardShow}
              onClick={onCardClick}
            />
          </SwiperSlide>
        ))}
      </Swiper>
    </section>
  )
}

const CuratedCard = ({
  item,
  index,
  onShow,
  onClick,
}: {
  item: ICuratedItem
  index: number
  onShow?: (item: ICuratedItem, index: number) => void
  onClick?: (item: ICuratedItem, index: number) => void
}) => {
  const cardRef = useRef<HTMLAnchorElement | null>(null)
  useTrackOnVisible({
    ref: cardRef,
    tracker: () => onShow?.(item, index),
    enabled: !!onShow,
  })

  return (
    <Link
      ref={cardRef}
      href={ROUTES.CAR_STORY.CONTENT_DETAIL(item.id)}
      onClick={() => onClick?.(item, index)}
      className='rounded-dimension-x5 relative block overflow-hidden'
    >
      <div className='bg-background-neutral-weak relative aspect-5/4 w-full overflow-hidden'>
        <OptimizedImage
          src={item.imageUrl}
          alt={item.title}
          width='100%'
          height='100%'
          radius={0}
          scaletype='crop'
          sizes='(min-width: 768px) 400px, 264px'
          fallback={<Image.Fallback />}
        />
      </div>
      <div
        className='px-dimension-x5 py-dimension-x4 h-20.75 flex items-center'
        style={{ backgroundColor: item.backgroundColor }}
      >
        <p
          className='text-title-1-semibold line-clamp-2 whitespace-pre-line'
          style={{ color: item.titleColor }}
        >
          {item.title}
        </p>
      </div>
    </Link>
  )
}

export default CuratedSwiperSection
